import IconButton from '@/components/common/icon-button';
import { ModalButton } from '@/components/common/modal-button';
import CloseIcon from '@/components/icons/CloseIcon';
import { MarkerIcon, RouteIcon } from '@/components/icons';
import { useScaleSize } from '@/hooks';
import { useVisibleBoothNumber } from '@/hooks/poi/useVisibleBoothNumber';
import { useVisibleBuildingName } from '@/hooks/poi/useVisibleBuildingName';
import { useFocusTrap } from '@/hooks/modal';
import { useConfigStore, useModalStackStore } from '@/stores';
import shopStyles from '@/components/feature/shop/shop-poi-modal/styles.module.scss';
import type { PoiContentSlide } from '@/types/shop';
import { formatPoiSlideToDetail } from '@/utils/floor-guide/formatPoiSlideToDetail';
import clsx from 'clsx';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { buildMinimalPoiModalTabOrders } from './buildTabOrders';
import modalStyles from './horizontal-poi-modal.module.scss';
import styles from './poi-minimal-modal.module.scss';

type Props = {
    isOpen: boolean;
    language: string;
    poiContent: PoiContentSlide | null;
    onClose: () => void;
    onLocationCheck: () => void;
    onGuideStart: () => void;
    showLocationCheck?: boolean;
};

export const MinimalPoiModal = ({ isOpen, language, poiContent, onClose, onLocationCheck, onGuideStart, showLocationCheck = true }: Props) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const { scaleWidth, scaleHeight } = useScaleSize();
    const { pushModal, popModal, isModalInStack } = useModalStackStore();
    const modalId = useId();
    const modalRef = useRef<HTMLDivElement>(null);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);

    const poiDetail = useMemo(() => formatPoiSlideToDetail(poiContent, language), [poiContent, language]);
    const tabOrders = useMemo(() => (poiDetail ? buildMinimalPoiModalTabOrders({ showLocationCheck }) : null), [poiDetail, showLocationCheck]);
    const buildingName = useVisibleBuildingName(poiDetail?.buildingName);
    const boothNumber = useVisibleBoothNumber(poiDetail?.boothNumber);

    useEffect(() => {
        if (isOpen) {
            if (!isModalInStack(modalId)) {
                pushModal(modalId);
            }
        } else if (isModalInStack(modalId)) {
            popModal(modalId);
        }

        return () => {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        };
    }, [isOpen, modalId, pushModal, popModal, isModalInStack]);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        if (prevActiveElementRef.current) {
            const elementToRestore = prevActiveElementRef.current;
            prevActiveElementRef.current = null;
            requestAnimationFrame(() => {
                if (document.body.contains(elementToRestore)) {
                    elementToRestore.focus({ preventScroll: false });
                }
            });
        }
        popModal(modalId);
        onClose();
    }, [modalId, onClose, popModal]);

    useFocusTrap(modalRef, isOpen, handleClose, false, modalId);

    if (!poiDetail || !tabOrders || !isOpen) {
        return null;
    }

    const closeButton = (
        <IconButton
            role="button"
            onClick={handleClose}
            ariaLabel={t('common.dialog.closeModal')}
            icon={<CloseIcon color="white" width={19} height={19} />}
            width={scaleWidth(80)}
            height={scaleHeight(80)}
            tabIndex={tabOrders.close}
            data-playsound="ting"
            data-interactive
            data-modal-region
            data-modal-region-order={tabOrders.close}
        />
    );

    return (
        <div className={modalStyles['floor-guide-horizontal-poi-modal__overlay']} style={{ zIndex: 60 }}>
            <div ref={modalRef} className={styles['minimal-poi-modal__stack']} role="dialog" aria-modal="true">
                <div className={clsx(styles['minimal-poi-modal__surface'])}>
                    <div className={styles['minimal-poi-modal__content']}>
                        <h1
                            data-interactive
                            data-modal-region
                            tabIndex={tabOrders.name}
                            data-modal-region-order={tabOrders.name}
                            aria-label={poiDetail.poiName}
                            className={styles['minimal-poi-modal__title']}
                        >
                            {poiDetail.poiName}
                        </h1>
                        <p
                            className={styles['minimal-poi-modal__floor']}
                            data-interactive
                            data-modal-region
                            tabIndex={tabOrders.floor}
                            data-modal-region-order={tabOrders.floor}
                            aria-label={`${buildingName ? `${poiDetail.floorName} ${buildingName}` : poiDetail.floorName}${boothNumber ? ` ${t('shop.search.tts.content.booth', { booth: boothNumber })}` : ''}`}
                        >
                            {buildingName ? `${poiDetail.floorName} · ${buildingName}` : poiDetail.floorName}
                            {boothNumber ? <span className={shopStyles['poi-modal__meta--booth']}>{boothNumber}</span> : null}
                        </p>
                    </div>
                    <div className={styles['minimal-poi-modal__actions']}>
                        {showLocationCheck && (
                            <ModalButton
                                size="fit"
                                variant="secondary"
                                className={styles['minimal-poi-modal__action-button']}
                                type="button"
                                role="button"
                                tabIndex={tabOrders.location}
                                data-modal-region
                                data-modal-region-order={tabOrders.location}
                                data-interactive
                                aria-label={t('shop.modal.text.buttons.location_check')}
                                icon={<MarkerIcon width={28} height={28} />}
                                onClick={onLocationCheck}
                                data-role={t('common.data_role.button')}
                            >
                                {t('shop.modal.text.buttons.location_check')}
                            </ModalButton>
                        )}
                        <ModalButton
                            size="fit"
                            variant="primary"
                            className={styles['minimal-poi-modal__action-button']}
                            type="button"
                            role="button"
                            tabIndex={tabOrders.guide}
                            data-modal-region
                            data-modal-region-order={tabOrders.guide}
                            data-interactive
                            aria-label={t('shop.modal.text.buttons.direction_guide')}
                            icon={<RouteIcon width={28} height={28} />}
                            onClick={onGuideStart}
                            data-role={t('common.data_role.button')}
                        >
                            {t('shop.modal.text.buttons.direction_guide')}
                        </ModalButton>
                    </div>
                </div>
                <div className={styles['minimal-poi-modal__close']}>{closeButton}</div>
            </div>
        </div>
    );
};
